import { useEffect, useRef, type FC } from 'react';
import { AfterShipAds } from '../../core/aftership-ads';
import { AFTERSHIP_ADS_SLOT_ID } from '../../core/config';
import { useAfterShipAds } from '../context/AfterShipAds.hook';
import type { AfterShipAdEventPayload, AfterShipAdSlotProps } from '../context/AfterShipAds.types';
import { observeClick, observeImpression, waitForCreative } from './AfterShipAdSlot.utils';

/**
 * An AfterShip ad mount point.
 *
 * Renders an empty <div> we own; the SDK appends its host element inside it and sizes
 * itself. Nothing here reserves space or blocks the page: the slot is empty until an ad
 * arrives, collapses to nothing when none does, and every failure path renders nothing.
 *
 * The container intentionally carries no height, max-height or overflow of its own — a
 * fixed height clips the creative.
 */
export const AfterShipAdSlot: FC<AfterShipAdSlotProps> = ({
  attributes,
  slotName,
  id = AFTERSHIP_ADS_SLOT_ID,
  className,
}) => {
  const { appKey, environment, logger, isAllowed, onImpression, onAdClick } = useAfterShipAds();
  const containerRef = useRef<HTMLDivElement>(null);
  const callbacksRef = useRef({ onImpression, onAdClick });

  // Analytics callbacks can change without changing the requested ad. Keep the latest
  // committed handlers while preserving the client and its per-ad event deduplication.
  useEffect(() => {
    callbacksRef.current = { onImpression, onAdClick };
  }, [onImpression, onAdClick]);

  // Ads are attributed using the context sent at request time, so a context change means a
  // fresh close() + show() rather than an update to the ad already on screen.
  const serializedAttributes = JSON.stringify(attributes);

  useEffect(() => {
    const container = containerRef.current;
    if (!isAllowed || !appKey || !container) return;

    let isCancelled = false;
    const cleanups: Array<() => void> = [];
    const ads = new AfterShipAds({ appKey, environment, logger });

    const payload: AfterShipAdEventPayload = {
      slotName: slotName ?? attributes.page_type,
      pageType: attributes.page_type,
      orderId: attributes.order_id,
    };

    /** Each event fires at most once per rendered ad. */
    const fireOnce = (handler?: (payload: AfterShipAdEventPayload) => void) => {
      let hasFired = false;
      return () => {
        if (hasFired) return;
        hasFired = true;
        handler?.(payload);
      };
    };

    const fireImpression = fireOnce((event) => callbacksRef.current.onImpression?.(event));
    const fireClick = fireOnce((event) => callbacksRef.current.onAdClick?.(event));

    const mountAd = async () => {
      // The mount element is already committed to the DOM by the time this effect runs,
      // which is what the SDK requires of create().
      const isCreated = await ads.create(container);
      if (!isCreated) return;

      // The teardown below already ran while create() was in flight (StrictMode effect
      // replay, or a route change), so its close() found no client. Close the one we just
      // created rather than leaving it bound to a detached mount.
      if (isCancelled) {
        ads.close();
        return;
      }

      const isShown = await ads.show(attributes);
      if (isCancelled) {
        ads.close();
        return;
      }
      if (!isShown) return;

      cleanups.push(
        waitForCreative(container, () => {
          if (isCancelled) return;
          cleanups.push(observeImpression(container, fireImpression));
          cleanups.push(observeClick(container, fireClick));
        }),
      );
    };

    mountAd();

    return () => {
      isCancelled = true;
      cleanups.forEach((cleanup) => cleanup());
      // Required on unmount / client-side route change so no stale ad frame is left behind.
      ads.close();
    };
    // `attributes` is compared by value via `serializedAttributes`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAllowed, appKey, environment, logger, serializedAttributes, slotName]);

  if (!isAllowed) return null;

  return <div id={id} ref={containerRef} className={className} data-testid="aftership-ad-slot" />;
};
